/**
 * Fonte do Pagamento — a REGRA, separada da chamada de rede que busca o catálogo.
 *
 * O catálogo vem de `business_properties.PAYMENT_SOURCES` pela rota `GET /billing/payment-sources`
 * (`listPaymentSources`, em `billingApi`). O que mora aqui é o que se FAZ com ele: qual opção vem
 * pré-selecionada e quem admite aval a devolver.
 *
 * ⚠️ Separado do `billingApi` de propósito. As telas mockam o `billingApi` inteiro nos testes, e
 * com os helpers lá dentro cada arquivo de teste precisava reescrever a lógica no mock — três
 * cópias à mão, livres para divergir da real sem ninguém perceber. Daqui elas chegam ao teste
 * DE VERDADE, porque ninguém mocka este módulo.
 */
export interface PaymentSourceOption {
    alias: string;
    name: string;
    /** habilita o "Aval a Devolver" — só o avalista admite, hoje */
    canGuaranteeRefund: boolean;
    /** charge_scenario em que esta fonte vem pré-selecionada (RECEIVABLE/GUARANTEE/INSTALLMENT) */
    defaultIf: string[];
}

/**
 * A fonte pré-selecionada para uma natureza de título.
 *
 * Duas telas precisam da mesma regra — o modal de sinalizar pagamento e o passo 3 da Identificação
 * — e duas cópias divergiriam no dia em que o seed mudasse.
 *
 * ⚠️ Natureza desconhecida cai na PRIMEIRA fonte do catálogo, e não em `''`. Antes devolvia vazio,
 * com o argumento de que era melhor o usuário escolher do que a tela afirmar por conta própria —
 * mas isso exigia uma opção "Selecione" que nunca era a resposta certa. Os selects deixaram de
 * oferecê-la, e um estado `''` sem opção correspondente mostraria a primeira opção na tela e
 * enviaria vazio no payload: a tela mentiria sobre o que vai mandar.
 *
 * `''` só sobra quando o catálogo veio vazio — aí não há o que pré-selecionar mesmo.
 */
export const defaultPaymentSource = (
    sources: PaymentSourceOption[], scenario?: string | null,
): string => {
    const byScenario = scenario
        ? sources.find(s => s.defaultIf.includes(scenario))?.alias
        : undefined;
    return byScenario || sources[0]?.alias || '';
};

/** A fonte escolhida admite aval a devolver? Alias desconhecido → não. */
export const allowsGuarantorRefund = (
    sources: PaymentSourceOption[], alias?: string | null,
): boolean => !!alias && !!sources.find(s => s.alias === alias)?.canGuaranteeRefund;
